import * as Sentry from "@sentry/react";

/**
 * Second layer of stale-chunk recovery, beneath the service worker precache.
 *
 * After a deploy, Vercel no longer serves the previous build's hashed assets, so
 * a tab opened before the deploy 404s when it lazy-loads a route. The precache
 * normally answers those requests; this covers the cases it cannot — a chunk
 * over the 5MB cap, a service worker that is not controlling the page yet, or an
 * evicted cache.
 *
 * Replaces a check for webpack's `error.name === "ChunkLoadError"`, which Vite
 * never produces. It also covers considerably more ground: the old handler only
 * ran inside `retryRoute` (2 of ~25 dynamic imports), whereas `vite:preloadError`
 * fires for every failed lazy import.
 */
const STORAGE_KEY = "force-reload-chunk";

let isReloadingPage = false;

/**
 * The two messages carry the asset differently, and only one of them is a full
 * URL:
 *
 * - the browser, on a failed JS import: `Failed to fetch dynamically imported
 *   module: https://host/assets/x.js`
 * - Vite, on a failed CSS preload: `Unable to preload CSS for /assets/x.css` —
 *   root-relative, because Vite's `assetsURL` only prepends the base
 *
 * Matching absolute URLs alone therefore silently skipped every stale CSS chunk.
 * Both are normalised against the origin so the `localStorage` guard below
 * compares one consistent form.
 */
function matchAssetReference(message: string): string | undefined {
  return message.match(
    /https?:\/\/[^\s"')]+|\/[^\s"')]+\.(?:js|mjs|css)\b/,
  )?.[0];
}

/** Normalised so the `localStorage` guard compares one consistent form. */
function normaliseAssetUrl(reference: string): string | undefined {
  try {
    return new URL(reference, window.location.origin).href;
  } catch {
    return undefined;
  }
}

// An allowlist, not "any message without an asset": `vite:preloadError` also
// fires when a chunk throws while evaluating, which a reload cannot fix.
const ASSETLESS_LOAD_FAILURES = [
  "Importing a module script failed", // WebKit
  "Failed to load module script", // Chromium, wrong MIME type
  "error loading dynamically imported module", // Firefox
];

type RecoveryKey = {
  /** What the one-shot `localStorage` guard compares. */
  guardValue: string;
  /** What `isRecoveredChunkPreloadError` looks for in a Sentry report. */
  suppressionMatch: string;
  asset: string | undefined;
};

function resolveRecoveryKey(message: string): RecoveryKey | undefined {
  const asset = matchAssetReference(message);
  if (asset !== undefined) {
    const url = normaliseAssetUrl(asset);
    return url === undefined
      ? undefined
      : { guardValue: url, suppressionMatch: asset, asset };
  }
  if (!ASSETLESS_LOAD_FAILURES.some((wording) => message.includes(wording))) {
    return undefined;
  }
  // A reload that works lands on a newer release, so the guard value changes.
  return {
    guardValue: `release:${import.meta.env.RELEASE_ID ?? "unknown"}`,
    suppressionMatch: message,
    asset: undefined,
  };
}

/**
 * Asset references a reload has already been triggered for, exactly as they
 * appeared in the error message. Only meaningful for the remainder of this page's
 * life, which is the window in which the rejection surfaces.
 */
const recoveredReferences = new Set<string>();

/**
 * Whether an error message describes a failed lazy import that a reload is
 * already recovering from, so Sentry can drop it.
 *
 * The rejection still propagates on purpose (see the note in the listener), which
 * means it reaches Sentry even when the reload fixes it — three such reports in
 * production turned out to be a stale chunk during the service worker's
 * activate-then-reload window, invisible to the user. The unrecoverable case is
 * reported explicitly below with `wasForceReloaded`, so nothing is lost.
 *
 * Matches on the raw reference rather than the normalised URL because that is
 * what the message text contains, and Vite's CSS variant is root-relative.
 */
export function isRecoveredChunkPreloadError(
  message: string | undefined,
): boolean {
  if (message === undefined) {
    return false;
  }
  for (const reference of recoveredReferences) {
    if (message.includes(reference)) {
      return true;
    }
  }
  return false;
}

/**
 * Context for working out *how* a stale chunk was reached, which the error alone
 * does not say.
 *
 * A missing chunk almost always means the service worker pruned its precache
 * moments earlier: activation deletes every entry outside the new manifest, and
 * the page keeps running until the reload it triggers commits. `navigationType`
 * separates a fresh load from an in-app history push, and `msSincePageLoad` shows
 * whether this landed in that early-load window or long after — which is the
 * difference between the reload paving over it and a user watching it happen.
 */
function collectDiagnostics(reference: string) {
  let navigationType = "unknown";
  let msSincePageLoad = -1;
  try {
    const [navigation] = (performance.getEntriesByType?.("navigation") ??
      []) as PerformanceNavigationTiming[];
    navigationType = navigation?.type ?? "unknown";
    msSincePageLoad = Math.round(performance.now?.() ?? -1);
  } catch {
    // Navigation timing is optional; recovery is not.
  }
  return {
    chunk: reference,
    navigationType,
    serviceWorker: navigator.serviceWorker?.controller?.scriptURL ?? "none",
    msSincePageLoad,
  };
}

export function installChunkPreloadErrorHandler() {
  window.addEventListener("vite:preloadError", (event) => {
    // Deliberately not calling `event.preventDefault()`. Vite only rethrows while
    // the event is uncancelled, and cancelling would make the import resolve with
    // `undefined` instead — so a route element would render from nothing and
    // crash more confusingly than the rejection does.
    const message = event.payload?.message;
    if (message === undefined) {
      return;
    }
    const recoveryKey = resolveRecoveryKey(message);
    if (recoveryKey === undefined) {
      return;
    }
    const { guardValue, suppressionMatch } = recoveryKey;

    if (isReloadingPage) {
      /**
       * A reload is already in flight, so this asset is being recovered as well
       * and must be marked. Chunks fail in batches — a single editor route can
       * lose a dozen at once — and the one that reaches an error boundary is
       * rarely the one that triggered the reload. Returning without marking here
       * showed the crash view and reported to Sentry for a failure the user never
       * experienced.
       */
      recoveredReferences.add(suppressionMatch);
      return;
    }

    const diagnostics = collectDiagnostics(recoveryKey.asset ?? message);
    try {
      // Rides along on whatever this page reports next, and stays attached across
      // renders. When the reload wins the race nothing further is reported, so
      // this is most useful for the unrecoverable branch below and for any
      // unrelated error that follows in the same page.
      Sentry.addBreadcrumb({
        category: "chunk-preload",
        level: "info",
        message: "Lazy import failed",
        data: diagnostics,
      });
      Sentry.setContext("staleChunk", diagnostics);
    } catch {
      // Nothing about reporting may stop the reload below.
    }

    try {
      if (localStorage.getItem(STORAGE_KEY) === guardValue) {
        // Already reloaded once for this key and it still fails, so a further
        // reload would loop. Report instead, and do not mark it recovered.
        Sentry.captureException(event.payload, {
          extra: {
            wasForceReloaded: true,
            reloadGuard: guardValue,
            ...diagnostics,
          },
        });
        return;
      }
      localStorage.setItem(STORAGE_KEY, guardValue);
      recoveredReferences.add(suppressionMatch);
      isReloadingPage = true;
      window.location.reload();
    } catch {
      // localStorage unavailable (private browsing, quota). Nothing to do.
    }
  });
}
